import * as React from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { useAtom } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { useAutoFocus } from '~shared/components/helpers';
import { SelectNumberRadioGroup } from '~shared/components/SelectNumberRadio/SelectNumberRadio';
import { toast } from '~shared/components/Toast';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { FileDrop } from '~teacher/components/FileDrop/FileDrop';
import {
  createNoOpHandler,
  createUploadToS3Handler,
} from '~teacher/components/FileDrop/FileHandlers';
import { FileHandlerConfigBuilder } from '~teacher/components/FileDrop/FileHandlers/FileHandlerConfig';
import { AiAssistedBadgeLink } from '~teacher/components/QuizEditor/AiAssistedBadgeLink';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { checkIsPromptEmpty } from '~teacher/components/QuizEditor/GenerateQuestionDialog/utils';
import { proCheck } from '~teacher/util/proCheck';

import {
  GenerateQuestionAction,
  checkIsQuestionTypeEmpty,
  generateQuestionAtom,
  generateQuestionPaywallAtom,
  promptAtom,
  questionCountOptions,
  useScopedStepAtom,
} from './atom';
import { GenerateQuestionPaywall } from './GenerateQuestionPaywall';
import { GenerateQuestionPrompt } from './GenerateQuestionPrompt';
import { useHandleGenerateQuestions } from './useHandleGenerateQuestion';

const MAX_FILES = 1;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

function useGenerateQuestion() {
  const [, changeStep] = useScopedStepAtom();
  const handleGenerateQuestions = useHandleGenerateQuestions();

  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
      fileKey,
    },
  ] = useAtom(generateQuestionAtom);

  const [prompt] = useAtom(promptAtom);
  const [, setPaywallOpen] = useAtom(generateQuestionPaywallAtom);

  const isPro = proCheck.isPro();

  return () => {
    if (
      checkIsQuestionTypeEmpty(multipleChoice, trueFalse, shortAnswers) ||
      (checkIsPromptEmpty(prompt) && !fileKey)
    ) {
      toast.critical(translate('Required field(s) missing'));
      return;
    }

    if (!isPro && fileKey) {
      setPaywallOpen(true);
      return;
    }

    handleGenerateQuestions();
    changeStep({ type: 'NextStep' });
  };
}

export function GenerateQuestionStep1() {
  const teacherApplicationContext = useTeacherApplicationContext();
  const autoFocus = useAutoFocus();
  const [uploadingState, setUploadingState] = React.useState('idle');
  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
      questionCount,
      includeExplanation,
    },
    dispatch,
  ] = useAtom(generateQuestionAtom);

  const generateQuestion = useGenerateQuestion();

  // MIME types are optional but recommended for more robust file type detection
  const fileHandlerConfig = useFileHandlerConfig(dispatch);

  const isPro = proCheck.isPro();

  return (
    <>
      <GenerateQuestionPaywall />
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <AiAssistedBadgeLink
                onClick={() => {
                  new FeatureUseEventAdapter({
                    feature: 'ai_assisted_link',
                    source: 'generate_questions_step_1',
                    action: 'click',
                  }).sendAnalytics();
                }}
              />
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700">
              {translate('Generate Questions')}
            </div>
            <div className="text-sans text-base leading-normal text-blue-600">
              {translate(
                'Create questions using a prompt and/or a file upload.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div
          className="flex justify-between flex-col pt-4 gap-2"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <GenerateQuestionPrompt ref={autoFocus} />
        </div>
        <div
          className="flex flex-col gap-4 pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <div className="flex items-center gap-3">
            <FileDrop
              isDisabled={
                !__DEV__ &&
                teacherApplicationContext ===
                  TeacherApplicationContext.Socrative
              }
              helperText={
                <FileDrop.HelperText>
                  {translate(
                    'Add context to your prompt by uploading a worksheet, report, excerpt, example quiz, or another learning material.'
                  )}
                </FileDrop.HelperText>
              }
              label={
                <FileDrop.Label
                  label={translate('Attach Document')}
                  badge={
                    <>
                      {teacherApplicationContext !==
                      TeacherApplicationContext.ShowbieAssessments ? (
                        <Badge tone="info">coming soon</Badge>
                      ) : !isPro ? (
                        <Badge tone="pro">{translate('Pro')}</Badge>
                      ) : null}
                    </>
                  }
                />
              }
              onUploadStateChange={setUploadingState}
              onFileRemove={() => {
                dispatch({ type: 'RemoveFileKey' });
              }}
              maxFiles={MAX_FILES}
              maxFileSize={MAX_FILE_SIZE}
              {...fileHandlerConfig}
            />
          </div>
        </div>
        <div
          className="flex flex-col gap-6 pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <div className="font-sans text-base text-blue-900">
            {translate('Question Type')}
          </div>
          <div className="flex flex-col gap-4 font-sans text-base">
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.MultipleChoice,
                    value: event.target.checked,
                  },
                });
              }}
              checked={multipleChoice}
              name={QuestionTypes.MultipleChoice}
            >
              <span>{translate('Multiple Choice')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.TrueFalse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={trueFalse}
              name={QuestionTypes.TrueFalse}
            >
              <span>{translate('True / False')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.FreeResponse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={shortAnswers}
              name={QuestionTypes.FreeResponse}
            >
              <span>{translate('Short Answer')}</span>
            </SlCheckbox>
          </div>
        </div>
        <div className="flex justify-between items-center gap-2 pt-5 pr-5 shadow-inset-border-t">
          <div className="flex flex-row items-center gap-2">
            <label
              className="text-base text-blue-900"
              htmlFor="generate-explanations-toggle"
              id="generate-explanations-label"
            >
              {translate('Generate explanations')}{' '}
            </label>
            <TooltipV2
              mode="click"
              contentWidth={260}
              label={translate(
                'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
              )}
            >
              <InformationIcon aria-hidden="true" />
            </TooltipV2>
          </div>
          <span id="generate-explanations-description" className="sr-only">
            {translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          </span>
          <ToggleSwitch
            labelledBy="generate-explanations-label"
            describedBy="generate-explanations-description"
            id="generate-explanations-toggle"
            size="small"
            on={includeExplanation}
            onChange={(value) => {
              dispatch({
                type: 'IncludeExplanationChange',
                payload: { includeExplanations: value },
              });
            }}
          />
        </div>
        <div className="pt-5 pr-5 shadow-inset-border-t">
          <div className="flex flex-row items-center justify-between gap-2">
            <label
              htmlFor="number-of-questions"
              className="text-base text-blue-900 leading-8"
              id="number-of-questions-label"
            >
              {translate('Number of Questions')}
            </label>
            <SelectNumberRadioGroup
              aria-labelledby={'number-of-questions-label'}
              name="numberOfQuestions"
              selectedNumber={questionCount}
              numberOptions={questionCountOptions}
              onChange={(number) => {
                dispatch({
                  type: 'NumberQuestionChange',
                  payload: { value: number },
                });
              }}
            />
          </div>
        </div>
        <div
          className="flex justify-end pt-8"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <SmallButton
            appearance="primary"
            onClick={generateQuestion}
            isDisabled={uploadingState !== 'idle'}
          >
            {translate('Generate')}
          </SmallButton>
        </div>
      </div>
    </>
  );
}

function useFileHandlerConfig(
  dispatch: React.Dispatch<GenerateQuestionAction>
) {
  const isPro = proCheck.isPro();

  // Free users: use no-op handler to "mock" file upload in the UI without any API call
  // Pro users: use real S3 upload
  // Paywall check happens when clicking the Generate button
  const onSuccess = (data: { fileKey: string; file?: File }) => {
    dispatch({
      type: 'AddFileKey',
      payload: {
        fileKey: data.fileKey,
        fileMetadata: data.file
          ? { type: data.file.type, size: data.file.size }
          : { type: 'unknown', size: 0 },
      },
    });
  };

  const onError = () => {
    toast.critical(translate('Failed to upload file'));
  };

  const handler = isPro
    ? createUploadToS3Handler({ onSuccess, onError })
    : createNoOpHandler({ onSuccess, onError });

  return new FileHandlerConfigBuilder()
    .addHandlerForGroups(handler, [
      { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
      { extensions: ['csv'], mimeTypes: ['text/csv'] },
      { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'] },
      { extensions: ['png'], mimeTypes: ['image/png'] },
    ])
    .build();
}
